import React, {useEffect, useRef, useState} from 'react';
import {RefreshControl, ScrollView, View} from 'react-native';
import {Image} from '@rneui/themed';
import {ThemeProvider} from '@rneui/themed';
import _ from 'lodash';
import {observer} from 'mobx-react';

import {MemberPriceCarousel} from '../../components/ProductCarousel';
import BenefitsList from '../../components/rewards/BenefitsList';
import ExclusiveOffers from '../../components/rewards/ExclusiveOffers';
import {JoinMoreRewardsCTA} from '../../components/rewards/JoinMoreRewards';
import JustForYou from '../../components/rewards/JustForYou';
import RewardsProgramImage from '../../components/rewards/ProgramImage';
import Promotions from '../../components/rewards/Promotions';
import ShopLoyaltyEnabled from '../../components/rewards/ShopLoyaltyEnabled';
import ViewMyCard from '../../components/rewards/ViewMyCard';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import Text from '../../components/themed/Text';
import ContentView from '../../components/ui-controls/layout/ContentView';
import WebFooter from '../../components/WebFooter';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {
  CONTENT_KEY_LOGGED_IN,
  CONTENT_KEY_LOGGED_OUT,
  CONTENT_KEY_SHOP_REWARDS_NOT_AVAILABLE,
  LoyaltyInformationItem,
} from '../../stores/loyaltyInformationStore';
import {useStores} from '../../stores/use-stores';
import rewardsTheme from '../../theme/rewardsTheme';
import {brandRewardsCopy, rewardsMarketingName} from '../../utils/rewards';
import {RewardsTermsAndConditionsPopper} from '../rewards/TermsAndConditions';

const RewardsListScreen = ({
  navigation,
}: RouteNavigatorScreenProps<AppRoute.REWARDS | AppRoute.ACCOUNT_REWARDS>) => {
  const rootStore = useStores();
  const {rewardsStore, userStore, shopSelectionStore} = useStores();
  const {isWebDesktopScreen} = useWebScreenDimensions();

  const [, setRefreshing] = React.useState(false);

  // Scroll button stuff
  const [showScrollButton, setShowScrollButton] = useState(false);
  const listRef = useRef() as any;

  const refresh = () => {
    setRefreshing(true);
    storeRef.current.load();
  };

  const loggedIn = userStore.isLoggedIn();

  const contentKey = !shopSelectionStore.loyaltyEnabled
    ? CONTENT_KEY_SHOP_REWARDS_NOT_AVAILABLE
    : loggedIn
    ? CONTENT_KEY_LOGGED_IN
    : CONTENT_KEY_LOGGED_OUT;

  const storeRef = useRef(new LoyaltyInformationItem(rootStore, contentKey));

  useEffect(() => {
    storeRef.current = new LoyaltyInformationItem(rootStore, contentKey);
    storeRef.current.load();
  }, [contentKey, rootStore]);

  const joinLoyaltyData = storeRef.current.data;

  return (
    <ContentView testID="rewards-screen">
      <ScrollView
        refreshControl={
          <RefreshControl refreshing={false} onRefresh={refresh} />
        }
        showsVerticalScrollIndicator={isWebDesktopScreen}
        showsHorizontalScrollIndicator={isWebDesktopScreen}
        // Handle scroll to top button
        onScrollBeginDrag={() => setShowScrollButton(true)}
        onMomentumScrollBegin={() => setShowScrollButton(true)}
        onMomentumScrollEnd={() => setShowScrollButton(false)}
        onScrollEndDrag={() => setShowScrollButton(false)}
        ref={listRef}>
        <ThemeProvider theme={rewardsTheme}>
          {shopSelectionStore.loyaltyEnabled && rewardsStore.hasRewards ? (
            /**
             * This is shown for when hasRewards true
             * shows rewards promotions available
             */
            <View
              style={
                isWebDesktopScreen
                  ? {
                      gap: 40,
                      marginTop: 20,
                    }
                  : {
                      gap: 24,
                      marginTop: 20,
                    }
              }>
              <View
                style={{
                  flexDirection: isWebDesktopScreen ? 'row' : 'column',
                  gap: isWebDesktopScreen ? 40 : 24,
                  marginBottom: isWebDesktopScreen ? 0 : 20,
                }}>
                <View
                  style={{
                    flexDirection: isWebDesktopScreen ? 'row' : 'column',
                    alignItems: 'center',
                    gap: isWebDesktopScreen ? 20 : 0,
                    flexGrow: 1,
                  }}>
                  <RewardsProgramImage variant="onLight" />
                  <Text style={{textAlign: 'center'}} variant="heading0">
                    WELCOME BACK TO {_.toUpper(rewardsMarketingName)}!
                  </Text>
                </View>
                <View
                  style={{
                    flexBasis: isWebDesktopScreen ? 230 : 'auto',
                    alignSelf: 'center',
                    width: '100%',
                  }}>
                  <ViewMyCard
                    name={userStore.name}
                    cardNumber={rewardsStore.cardNumber}
                    inStoreCode={rewardsStore.shortcode}
                    walletUrl={rewardsStore.walletUrl}
                    mode={isWebDesktopScreen ? 'print' : 'screen'}
                    />
                </View>
              </View>

              <MemberPriceCarousel
                title={brandRewardsCopy.memberPublicPricingTitle.toUpperCase()}
                member_price="public"
                onSeeAllPress={() => {
                  navigation.navigate(AppRoute.MEMBERS_PRICE_LIST, {
                    external_sell_type: 'members-price',
                  });
                }}
              />

              <JustForYou
                onSeeAllPress={() => {
                  navigation.navigate(AppRoute.MEMBERS_PRICE_LIST, {
                    external_sell_type: 'just-for-you',
                  });
                }}
              />

              <ExclusiveOffers
                onSeeAllPress={() => {
                  navigation.navigate(AppRoute.EXCLUSIVE_OFFER_LIST);
                }}
              />

              <Promotions
                onSeeAllPress={() => {
                  navigation.navigate(AppRoute.PROMOTIONS_CURRENT);
                }}
              />
            </View>
          ) : joinLoyaltyData ? (
            /**
             * This is shown for when hasRewards false
             * for logged in or logged out user
             * showing content to encourage user to join and includes a join now CTA
             */
            <View
              style={
                isWebDesktopScreen
                  ? {
                      gap: 40,
                      marginTop: 40,
                    }
                  : {
                      gap: 24,
                    }
              }>
              <View>
                <Image
                  source={{
                    uri: isWebDesktopScreen
                      ? joinLoyaltyData?.imageDesktop?.url
                      : joinLoyaltyData?.image?.url,
                  }}
                  containerStyle={{
                    width: '100%',
                    aspectRatio: 1.54,
                    flex: 1,
                    flexGrow: 1,
                    borderRadius: 10,
                    ...(isWebDesktopScreen ? {maxHeight: 400} : {}),
                  }}
                />
                <Text variant="heading1">{joinLoyaltyData.headline}</Text>
                <Text variant="body2">{joinLoyaltyData.headlineCopy}</Text>

                <ShopLoyaltyEnabled>
                  <View style={{marginVertical: 24}}>
                    <JoinMoreRewardsCTA navigation={navigation} />
                  </View>
                </ShopLoyaltyEnabled>
              </View>

              <ShopLoyaltyEnabled>
                <MemberPriceCarousel
                  title={`UNLOCK ${brandRewardsCopy.memberPublicPricingTitle.toUpperCase()}`}
                  member_price="public"
                  onSeeAllPress={() => {
                    navigation.navigate(AppRoute.MEMBERS_PRICE_LIST, {
                      external_sell_type: 'members-price',
                    });
                  }}
                />
              </ShopLoyaltyEnabled>

              <View>
                <BenefitsList {...joinLoyaltyData} />

                <View style={{flexDirection: 'row'}}>
                  <Text variant="body2">{rewardsMarketingName} </Text>
                  <RewardsTermsAndConditionsPopper />
                </View>
              </View>
            </View>
          ) : null}
        </ThemeProvider>

        {/* TODO: The WebFooter should be in a global/common place */}
        {!!isWebDesktopScreen && (
          <>
            <View style={{flexGrow: 1, height: 100}} />
            <WebFooter />
          </>
        )}
      </ScrollView>

      <ScrollToTopButton
        open={showScrollButton}
        callback={() => listRef.current.scrollTo({x: 0, y: 0, animated: true})}
      />
    </ContentView>
  );
};

export default observer(RewardsListScreen);
